<template>
  <LayoutMain class="my-personals">
    <PersonalsTopBar :showCreateCTA="true" />

    <PageHeader
      :title="t('personals.myAds')"
      :subtitle="t('personals.myAdsSubtitle', { active: activeAdsCount })"
    >
      <template #actions>
        <Button variant="primary" size="sm" :disabled="!canCreateAd" @click="openCreate">
          <Plus :size="16" class="mr-1" />
          {{ t('personals.createAd') }}
        </Button>
      </template>
    </PageHeader>

    <ErrorDisplay v-if="error" :message="error" class="mb-4" />

    <div v-if="isLoading" class="py-8">
      <LoadingSpinner size="lg" />
    </div>

    <div v-else>
      <!-- Success/Info messages -->
      <div v-if="deleteMessage" class="success-message">{{ deleteMessage }}</div>

      <!-- Empty state -->
      <div v-if="!myAds.length" class="empty-state">
        <Card variant="glass" class="p-8 text-center space-y-3">
          <Text variant="body" class="text-neutral-600">{{ t('personals.noMyAds') }}</Text>
          <Button variant="primary" :disabled="!canCreateAd" @click="openCreate">
            <Plus :size="16" class="mr-1" />
            {{ t('personals.createAd') }}
          </Button>
        </Card>
      </div>

      <!-- Ads list -->
      <div v-else class="space-y-4">
        <Card v-for="ad in myAds" :key="ad.id" variant="glass" class="ad-card">
          <template #header>
            <div class="flex items-start justify-between gap-4">
              <div class="space-y-1">
                <Heading level="3" class="text-lg font-semibold text-forest-primary">
                  {{ ad.title }}
                </Heading>
                <div class="flex items-center gap-3 text-neutral-600">
                  <div class="flex items-center gap-1">
                    <Clock :size="14" />
                    <Text variant="caption">
                      {{ formatTimeAgo(ad.created_at) }}
                    </Text>
                  </div>
                  <div class="flex items-center gap-1 text-emerald-600">
                    <Tag :size="14" />
                    <Text variant="caption">
                      {{ t('personals.active') }}
                    </Text>
                  </div>
                </div>
              </div>
            </div>
          </template>

          <!-- Body -->
          <Text variant="body" class="text-neutral-700 whitespace-pre-wrap">
            {{ ad.body }}
          </Text>

          <!-- Footer with actions at the bottom -->
          <template #footer>
            <div
              class="pt-3 border-t flex flex-wrap items-start gap-2 gap-y-3 justify-between pb-4 sm:pb-3"
              style="border-color: var(--gradient-linen)"
            >
              <!-- Left info -->
              <div class="flex items-center gap-2 text-neutral-600 min-w-[8rem]">
                <Clock :size="14" />
                <Text variant="caption">
                  {{ t('personals.posted') }} {{ formatTimeAgo(ad.created_at) }}
                </Text>
              </div>

              <!-- Actions: wrap on small screens, avoid overflow -->
              <div
                class="ad-actions flex flex-wrap w-full sm:w-auto sm:flex-nowrap sm:justify-end gap-2"
              >
                <Button
                  variant="ghost"
                  size="sm"
                  class="!px-3 !py-1.5 flex-1 sm:flex-none min-w-[9.5rem]"
                  @click="openEdit(ad)"
                >
                  <Edit3 :size="14" class="mr-1 inline-block align-middle" />
                  <span class="align-middle">{{ t('common.actions.edit') }}</span>
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  class="!px-3 !py-1.5 flex-1 sm:flex-none min-w-[9.5rem] text-red-600 hover:text-red-700"
                  @click="confirmDelete(ad)"
                >
                  <Trash2 :size="14" class="mr-1 inline-block align-middle" />
                  <span class="align-middle">{{ t('common.actions.delete') }}</span>
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  class="!px-3 !py-1.5 flex-1 sm:flex-none min-w-[9.5rem]"
                  @click="viewAdReplies(ad.id)"
                >
                  <MessageCircle :size="14" class="mr-1 inline-block align-middle" />
                  <span class="align-middle">{{ t('personals.viewReplies') }}</span>
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  class="!px-3 !py-1.5 flex-1 sm:flex-none min-w-[9.5rem]"
                  @click="navigateToAd(ad.id)"
                >
                  <Eye :size="14" class="mr-1 inline-block align-middle" />
                  <span class="align-middle">{{ t('personals.viewAd') }}</span>
                </Button>
              </div>
            </div>
          </template>
        </Card>
      </div>
    </div>

    <!-- Create/Edit Dialog -->
    <Dialog
      v-model="showDialog"
      :title="dialogMode === 'create' ? t('personals.createAd') : t('personals.editAd')"
      width="640px"
      @close="resetForm"
    >
      <div class="space-y-4">
        <div>
          <Label for="title">{{ t('personals.formTitle') }}</Label>
          <Input
            id="title"
            v-model="form.title"
            :placeholder="t('personals.titlePlaceholder')"
            :error="!!formErrors.title"
          />
          <Text v-if="formErrors.title" variant="caption" class="text-red-600">{{
            formErrors.title
          }}</Text>
        </div>

        <div>
          <Label for="description">{{ t('personals.description') }}</Label>
          <Textarea
            id="description"
            v-model="form.body"
            rows="6"
            :placeholder="t('personals.descriptionPlaceholder')"
            :error="!!formErrors.body"
          />
          <Text v-if="formErrors.body" variant="caption" class="text-red-600">{{
            formErrors.body
          }}</Text>
        </div>
      </div>

      <template #footer>
        <div class="flex justify-end gap-2">
          <Button variant="ghost" @click="closeDialog">{{ t('common.actions.cancel') }}</Button>
          <Button variant="primary" :disabled="saving" @click="handleSave">
            <LoadingSpinner v-if="saving" size="sm" class="mr-1" />
            {{ dialogMode === 'create' ? t('common.actions.save') : t('common.actions.save') }}
          </Button>
        </div>
      </template>
    </Dialog>

    <!-- Delete confirm Dialog -->
    <Dialog v-model="showDeleteConfirm" :title="t('common.actions.delete')" width="480px">
      <div class="space-y-3">
        <Text variant="body">{{
          t('personals.adsLimitDescription', { current: activeAdsCount, max: 3 })
        }}</Text>
        <Text variant="body" class="text-red-600">{{
          t('common.areYouSure') || 'Are you sure you want to delete this ad?'
        }}</Text>
      </div>
      <template #footer>
        <div class="flex justify-end gap-2">
          <Button variant="ghost" @click="showDeleteConfirm = false">{{
            t('common.actions.cancel')
          }}</Button>
          <Button
            variant="primary"
            class="bg-red-600 hover:bg-red-700 border-0"
            :disabled="saving"
            @click="handleDeleteConfirmed"
          >
            <LoadingSpinner v-if="saving" size="sm" class="mr-1" />
            {{ t('common.actions.delete') }}
          </Button>
        </div>
      </template>
    </Dialog>
  </LayoutMain>
</template>

<script setup>
import { onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter, useRoute } from 'vue-router'
import { useMyPersonals } from '@/composables/usePersonals'
import { Plus, MessageCircle, Eye, Trash2, Edit3, Clock, Tag } from 'lucide-vue-next'

import PersonalsTopBar from '../components/personals/PersonalsTopBar.vue'
import PageHeader from '../components/ui/PageHeader.vue'
import LayoutMain from '../components/ui/LayoutMain.vue'
import Button from '../components/ui/Button.vue'
import Card from '../components/ui/Card.vue'
import Text from '../components/ui/Text.vue'
import Heading from '../components/ui/Heading.vue'
import Badge from '../components/ui/Badge.vue'
import LoadingSpinner from '../components/ui/LoadingSpinner.vue'
import ErrorDisplay from '../components/ui/ErrorDisplay.vue'
import Dialog from '../components/ui/Dialog.vue'
import Input from '../components/ui/Input.vue'
import Textarea from '../components/ui/Textarea.vue'
import Label from '../components/ui/Label.vue'

const router = useRouter()
const route = useRoute()
const { t } = useI18n()
const {
  myAds,
  isLoading,
  error,
  canCreateAd,
  activeAdsCount,
  loadMyAds,
  deleteAd,
  viewAdReplies,
  navigateToAd
} = useMyPersonals()

// Local UI state
const deleteMessage = ref('')
const showDialog = ref(false)
const dialogMode = ref('create') // 'create' | 'edit'
const saving = ref(false)
const showDeleteConfirm = ref(false)
const adPendingDelete = ref(null)

const form = ref({
  id: null,
  title: '',
  body: ''
})
const formErrors = ref({
  title: '',
  body: ''
})

// Navigation helpers
function openCreate() {
  dialogMode.value = 'create'
  resetForm()
  showDialog.value = true
}
function openEdit(ad) {
  dialogMode.value = 'edit'
  form.value = {
    id: ad.id,
    title: ad.title || '',
    body: ad.body || ''
  }
  formErrors.value = { title: '', body: '' }
  showDialog.value = true
}
function closeDialog() {
  showDialog.value = false
}
function resetForm() {
  form.value = { id: null, title: '', body: '' }
  formErrors.value = { title: '', body: '' }
}

// Validation
function validate() {
  formErrors.value = { title: '', body: '' }
  if (!form.value.title.trim())
    formErrors.value.title = t('common.validation.required') || 'Required'
  if (!form.value.body.trim()) formErrors.value.body = t('common.validation.required') || 'Required'
  return !formErrors.value.title && !formErrors.value.body
}

// Save handler
async function handleSave() {
  if (!validate()) return
  saving.value = true
  try {
    if (dialogMode.value === 'create') {
      // create via store
      const { usePersonalsStore } = await import('@/stores/personals')
      const store = usePersonalsStore()
      const created = await store.createPersonalAd({
        title: form.value.title,
        body: form.value.body
      })
      // Refresh my ads list if empty state or ensure newest appears
      await loadMyAds()
    } else {
      // update via store
      const { usePersonalsStore } = await import('@/stores/personals')
      const store = usePersonalsStore()
      await store.updatePersonalAd(form.value.id, {
        title: form.value.title,
        body: form.value.body
      })
      await loadMyAds()
    }
    closeDialog()
  } catch (e) {
    // errors are captured in store; optionally surface message
  } finally {
    saving.value = false
  }
}

// Delete confirm flow
function confirmDelete(ad) {
  adPendingDelete.value = ad
  showDeleteConfirm.value = true
}
async function handleDeleteConfirmed() {
  if (!adPendingDelete.value) return
  saving.value = true
  try {
    const { usePersonalsStore } = await import('@/stores/personals')
    const store = usePersonalsStore()
    await store.deleteAd(adPendingDelete.value.id)
    deleteMessage.value = t('common.actions.deleted') || 'Deleted'
    await loadMyAds()
  } catch (e) {
    // already handled in store
  } finally {
    saving.value = false
    showDeleteConfirm.value = false
    adPendingDelete.value = null
  }
}

// Existing utilities
function formatTimeAgo(dateString) {
  const date = new Date(dateString)
  const now = new Date()
  const diffInDays = Math.floor((now - date) / (1000 * 60 * 60 * 24))
  if (diffInDays === 0) return t('common.time.today')
  if (diffInDays === 1) return t('common.time.yesterday')
  if (diffInDays < 7) return t('common.time.daysAgo', { count: diffInDays })
  if (diffInDays < 30) return t('common.time.weeksAgo', { count: Math.floor(diffInDays / 7) })
  return date.toLocaleDateString()
}

// Watch for route changes to reload data
watch(
  () => route.path,
  newPath => {
    if (newPath === '/my/personals') {
      loadMyAds()
    }
  },
  { immediate: true }
)

// Lifecycle
onMounted(async () => {
  await loadMyAds()
})
</script>

<style scoped>
.my-personals {
  min-height: 100vh;
}

.ad-card {
  transition: all 0.2s ease;
}
.ad-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.success-message {
  background: var(--emerald-50);
  color: var(--emerald-700);
  padding: 0.75rem;
  border-radius: 0.5rem;
  border: 1px solid var(--emerald-200);
  margin-bottom: 1rem;
}
.empty-state {
  text-align: center;
  padding: 3rem 1rem;
}
</style>
